/*
 * GoogleSignInButton — the one-tap door, styled to Google's brand guidance
 * (white surface, official multi-colour G, Roboto-ish weight) so it reads as
 * a trusted system control rather than a HAVN button.
 *
 * Sized generously: 52px tall, 16px label — comfortable for older hands on
 * the senior-facing pages too.
 */

import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';

function GoogleG({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24s.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

export default function GoogleSignInButton({
  label = 'Continue with Google',
  redirectTo,
  onError,
  style = {},
}) {
  const { signInWithGoogle } = useAuth();
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    setBusy(true);
    try {
      // Sends the browser to Google; the page unmounts on success.
      await signInWithGoogle(redirectTo);
    } catch (err) {
      setBusy(false);
      if (onError) onError(err?.message || 'Google sign-in is unavailable right now.');
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      data-testid="google-signin"
      style={{
        width: '100%',
        minHeight: '52px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        padding: '14px 18px',
        background: '#fff',
        color: '#3c4043',
        border: '1px solid #dadce0',
        borderRadius: '10px',
        fontSize: '16px',
        fontWeight: 600,
        cursor: busy ? 'wait' : 'pointer',
        opacity: busy ? 0.7 : 1,
        transition: 'box-shadow 0.2s',
        ...style,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 1px 6px rgba(0,0,0,0.25)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      <GoogleG />
      {busy ? 'Opening Google…' : label}
    </button>
  );
}

/** "or" divider, for placing the button above an email form. */
export function AuthDivider({ label = 'or', color = 'rgba(255,255,255,0.35)' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0' }}>
      <div style={{ flex: 1, height: '1px', background: 'currentColor', opacity: 0.18, color }} />
      <span style={{ fontSize: '13px', color }}>{label}</span>
      <div style={{ flex: 1, height: '1px', background: 'currentColor', opacity: 0.18, color }} />
    </div>
  );
}
